import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import { getStatusTranslation } from '../components/prior-authorization/components/PriorAuthStatusDescriptions';
import { getFragment } from '../graphql';
import type { PriorAuthorizationInfoFragment } from '../graphql/generated/graphql';
import { getAbsoluteRoute } from '../util';
import {
    type BasicPrescriberOrderFragment,
    getMedicationDescription,
    getPrescribedProductFragment,
    isUnclaimedPrescriberOrder,
} from '../util/cabinet/prescriber-orders';
import { isBasicPrescriberOrder } from '../util/orders';

import { useFlags } from './use-flags';
import { usePriorAuthorizationDetails } from './use-prior-authorization-details';

export const useMedicationOrderDetails = (
    order: BasicPrescriberOrderFragment | PriorAuthorizationInfoFragment
) => {
    const { drugDetailsSplitEnabled } = useFlags();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [
        showUnclaimedModal,
        { open: openUnclaimedModal, close: closeUnclaimedModal },
    ] = useDisclosure(false);

    const isUnclaimed = isBasicPrescriberOrder(order)
        ? isUnclaimedPrescriberOrder(order)
        : false;
    const prescriberOrderId = isBasicPrescriberOrder(order)
        ? (order.drxId ?? order.id)
        : null;

    const { isNonDrxPriorAuthorization, priorAuthorizationDetails } =
        usePriorAuthorizationDetails(order);

    const fillOrder = useCallback(() => {
        if (prescriberOrderId) {
            navigate(
                getAbsoluteRoute('cabinet.rxId.root', {
                    prescriptionId: prescriberOrderId,
                })
            );
        }
    }, [prescriberOrderId, navigate]);

    const verifyOrder = useCallback(() => {
        if (prescriberOrderId) {
            navigate(
                getAbsoluteRoute('cabinet.rxId.verifyPatient', {
                    prescriptionId: prescriberOrderId,
                })
            );
        }
    }, [prescriberOrderId, navigate]);

    const prescribedProduct = isBasicPrescriberOrder(order)
        ? getFragment(getPrescribedProductFragment(order))
        : null;
    const nonDrxPaProduct = getFragment(priorAuthorizationDetails?.medication);
    const productForm =
        prescribedProduct?.__typename === 'Product'
            ? prescribedProduct?.formValue
            : nonDrxPaProduct?.formCode;

    const description = isBasicPrescriberOrder(order)
        ? getMedicationDescription(order)
        : isNonDrxPriorAuthorization
          ? t('priorAuth.nonDrxPa.description', {
                nonDrxDescription: nonDrxPaProduct?.name,
            })
          : null;

    const clickHandler = useMemo(() => {
        if (isUnclaimed) {
            return openUnclaimedModal;
        }

        if (prescriberOrderId) {
            return () =>
                navigate(
                    getAbsoluteRoute('orders.prescriberOrderId.details', {
                        prescriberOrderId,
                    }),
                    { state: { prev: 'history' } }
                );
        }

        if (priorAuthorizationDetails) {
            return () =>
                navigate(
                    getAbsoluteRoute(
                        'priorAuthorization.priorAuthorizationId.details',
                        {
                            priorAuthorizationId: priorAuthorizationDetails.id,
                        }
                    ),
                    { state: { prev: 'history' } }
                );
        }

        return undefined;
    }, [
        prescriberOrderId,
        isUnclaimed,
        openUnclaimedModal,
        priorAuthorizationDetails,
        navigate,
    ]);

    const navigateToPriorAuthStatus = useCallback(() => {
        if (priorAuthorizationDetails) {
            navigate(
                getAbsoluteRoute(
                    'priorAuthorization.priorAuthorizationId.status',
                    {
                        priorAuthorizationId: priorAuthorizationDetails.id,
                    }
                ),
                prescriberOrderId ? { state: { prescriberOrderId } } : undefined
            );
        }
    }, [priorAuthorizationDetails, prescriberOrderId, navigate]);

    const patientName = isNonDrxPriorAuthorization
        ? getFragment(priorAuthorizationDetails?.patient)?.fullName
        : isBasicPrescriberOrder(order) && !isUnclaimedPrescriberOrder(order)
          ? getFragment(order.patient)?.fullName
          : null;

    const status = priorAuthorizationDetails?.status;
    const translatedStatus = status && getStatusTranslation(status);
    const shouldShowPaStatus = !!priorAuthorizationDetails;

    return {
        productForm,
        description,
        drugDetailsSplitEnabled,
        prescribedProduct,
        clickHandler,
        isUnclaimed,
        patientName,
        shouldShowPaStatus,
        status: translatedStatus,
        navigateToPriorAuthStatus,
        fillOrder,
        verifyOrder,
        showUnclaimedModal,
        closeUnclaimedModal,
    };
};
